import type Decimal from "decimal.js";

import { D_1, D0, D1, D2, DPI } from "./constants";
import { clamp } from "./math";

/**
 * Custom engine three dimensional vector using `Decimal` numbers.
 *
 * We often use `VectorD3` on the battlefield: all the mathematical methods (AKA CPU intensive methods)
 * handle this specific case to be a bit more efficient.
 */
export class VectorD3 {
  /** Creates a new `VectorD3` with specified components or zero if omitted. */
  constructor(
    public x = D0,
    public y = D0,
    public z = D0
  ) { }

  /** Sets the components to the given values. */
  set(x: Decimal, y: Decimal, z: Decimal) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  /** Sets the x and y components to the given values. */
  setXY(x: Decimal, y: Decimal) {
    this.x = x;
    this.y = y;

    return this;
  }

  /** Creates a new `VectorD3` cloning the original one's components. */
  clone() {
    return new VectorD3(this.x, this.y, this.z);
  }

  /** Copies the components of the target `VectorD3` into this ones. */
  copy(vector: VectorD3) {
    this.x = vector.x;
    this.y = vector.y;
    this.z = vector.z;

    return this;
  }

  /** Adds the target `VectorD3` to this one. */
  add(vector: VectorD3) {
    this.x = this.x.plus(vector.x);
    this.y = this.y.plus(vector.y);
    if(this.z !== D0 || vector.z !== D0) this.z = this.z.plus(vector.z);

    return this;
  }

  /** Subtracts the target `VectorD3` from this one. */
  sub(vector: VectorD3) {
    this.x = this.x.minus(vector.x);
    this.y = this.y.minus(vector.y);
    if(this.z !== D0 || vector.z !== D0) this.z = this.z.minus(vector.z);

    return this;
  }

  rotate90() {
    const oldY = this.y;
    this.y = this.x;
    this.x = oldY.mul(-1);

    return this;
  }

  /** Sums two `VectorD3`s and returns a new one with the resulting value. */
  static add(a: VectorD3, b: VectorD3) {
    return new VectorD3(a.x.plus(b.x), a.y.plus(b.y), a.z === D0 && b.z === D0 ? D0 : a.z.plus(b.z));
  }

  /** Subtracts `b` from `a` and returns a new `VectorD3` with the resulting value. */
  static sub(a: VectorD3, b: VectorD3) {
    return new VectorD3(a.x.minus(b.x), a.y.minus(b.y), a.z === D0 && b.z === D0 ? D0 : a.z.minus(b.z));
  }

  /** Returns the distance from the given `VectorD3`. */
  distanceTo(v: VectorD3) {
    return this.distanceToSquared(v).squareRoot();
  }

  /**
   * Returns the squared distance from the given `VectorD3`.
   *
   * When comparing the distance with another distance, to compare the distance squared instead is slightly more efficient.
   */
  distanceToSquared(v: VectorD3) {
    const dx = this.x.minus(v.x);
    const dy = this.y.minus(v.y);
    const dxySquared = dx.mul(dx).plus(dy.mul(dy));

    if(this.z === D0 && v.z === D0) return dxySquared;

    const dz = this.z.minus(v.z);

    return dxySquared.plus(dz.mul(dz));
  }

  /** Calculates the dot product with the given vector. */
  dot(vector: VectorD3) {
    const dPxy = this.x.mul(vector.x).plus(this.y.mul(vector.y));

    return this.z === D0 && vector.z === D0 ? dPxy : dPxy.plus(this.z.mul(vector.z));
  }

  /**
   * Computes the square of the Euclidean length (straight-line length) from (0, 0, 0) to (x, y, z).
   *
   * When comparing the lengths of vectors, to compare the length squared instead is slightly more efficient.
   */
  lengthSquared() {
    return this.dot(this);
  }

  /**
   * Computes the Euclidean length (straight-line length) from (0, 0, 0) to (x, y, z).
   */
  length() {
    return this.lengthSquared().squareRoot();
  }

  /** Multiplies the given scalar value with all components of this instance. */
  multiplyScalar(scalar: Decimal) {
    this.x = this.x.mul(scalar);
    this.y = this.y.mul(scalar);
    if(this.z !== D0) this.z = this.z.mul(scalar);

    return this;
  }

  /** Divides this vector by the given scalar. */
  divideScalar(scalar: Decimal) {
    this.x = this.x.div(scalar);
    this.y = this.y.div(scalar);
    if(this.z !== D0) this.z = this.z.div(scalar);

    return this;
  }

  /** Converts this vector to a unit vector i.e. sets it equal to a vector with the same direction, but with a length of `1`. */
  normalize() {
    return this.divideScalar(this.length());
  }

  /** Returns the angle between the given vector and this instance in radians. */
  angleTo(vector: VectorD3) {
    const denominator = this.lengthSquared().mul(vector.lengthSquared()).squareRoot();

    if(denominator.isZero()) return DPI.div(D2);

    const theta = this.dot(vector).div(denominator);

    return clamp(theta, D_1, D1).acos();
  }

  /** Returns a string representation of the vector in the format "(x, y, z)". */
  toString() {
    return `(${this.x.toString()}, ${this.y.toString()}, ${this.z.toString()})`;
  }
}

export const ORIGIN = new VectorD3();

/** Moves an array of `VectorD3` on the battlefield of the given offset and rotates it of the given angle. */
export const transform2DVectors = (from: VectorD3[], offset: VectorD3, angle: Decimal): VectorD3[] => {
  const { x: offsetX, y: offsetY } = offset;

  const to: VectorD3[] = [];
  if(angle === D0) for(const v of from) to.push(v.clone().setXY(v.x.plus(offsetX), v.y.plus(offsetY)));
  else {
    const cos = angle.cos();
    const sin = angle.sin();

    for(const v of from) {
      const { x, y } = v;

      to.push(v.clone().setXY(x.mul(cos).minus(y.mul(sin)).plus(offsetX), x.mul(sin).plus(y.mul(cos)).plus(offsetY)));
    }
  }
  return to;
};

export const transform2DVector = (from: VectorD3, offset: VectorD3, angle:Decimal): VectorD3 => { 
  const { x: offsetX, y: offsetY } = offset;

  const to: VectorD3 = new VectorD3();
  if(angle === D0) 
    to.setXY(from.x.plus(offsetX), from.y.plus(offsetY));
  else {
    const cos = angle.cos();
    const sin = angle.sin();

    const { x, y } = from;

    to.setXY(x.mul(cos).minus(y.mul(sin)).plus(offsetX), x.mul(sin).plus(y.mul(cos)).plus(offsetY));
    
  }
  return to;
};
